export interface MapWorkbenchSpiderifyChooserFeatureLike {
  id?: string | number;
  sourceLayer?: string;
  layerId?: string;
  properties?: Record<string, unknown>;
}

export interface MapWorkbenchSpiderifyChooserListItem {
  key: string;
  title: string;
  subtitle: string;
  details: Array<{ label: string; value: string }>;
}

export interface MapWorkbenchSpiderifyResolvedSummaryLike {
  title: string;
  subtitle?: string | null;
  summary?: Array<{ key: string; label: string; value: string }>;
}

const TITLE_FIELDS = [
  'name',
  'title',
  'label',
  'display_name',
  'site_name',
  'location_name',
  'address',
];

const DETAIL_EXCLUDE_FIELDS = new Set([
  ...TITLE_FIELDS,
  'id',
  'objectid',
  'object_id',
  'fid',
  'shape',
  'geometry',
]);

const formatFieldLabel = (field: string) =>
  field
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, character => character.toUpperCase());

const stringifyFieldValue = (value: unknown) => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

const selectTitleValue = (feature: MapWorkbenchSpiderifyChooserFeatureLike) => {
  const properties = feature.properties ?? {};
  for (const field of TITLE_FIELDS) {
    const value = stringifyFieldValue(properties[field]);
    if (value) {
      return { field, value };
    }
  }
  const firstScalarEntry = Object.entries(properties).find(([, value]) => Boolean(stringifyFieldValue(value)));
  if (firstScalarEntry) {
    return {
      field: firstScalarEntry[0],
      value: stringifyFieldValue(firstScalarEntry[1]),
    };
  }
  if (feature.id != null) {
    return { field: null, value: `Feature ${String(feature.id)}` };
  }
  return { field: null, value: feature.sourceLayer ?? feature.layerId ?? 'Visible feature' };
};

const selectDetails = (feature: MapWorkbenchSpiderifyChooserFeatureLike, titleField: string | null) => {
  const properties = feature.properties ?? {};
  return Object.entries(properties)
    .filter(
      ([field, value]) =>
        field !== titleField && !DETAIL_EXCLUDE_FIELDS.has(field) && Boolean(stringifyFieldValue(value)),
    )
    .slice(0, 2)
    .map(([field, value]) => ({
      label: formatFieldLabel(field),
      value: stringifyFieldValue(value),
    }));
};

export const buildMapWorkbenchSpiderifyChooserItems = (
  features: MapWorkbenchSpiderifyChooserFeatureLike[],
): MapWorkbenchSpiderifyChooserListItem[] =>
  features.map((feature, index) => {
    const title = selectTitleValue(feature);
    const sourceLabel = feature.sourceLayer ?? feature.layerId ?? 'Visible feature';
    const idLabel = feature.id != null ? `ID ${String(feature.id)}` : null;
    return {
      key: `${sourceLabel}:${feature.id ?? index}:${index}`,
      title: title.value,
      subtitle: idLabel ? `${sourceLabel} · ${idLabel}` : sourceLabel,
      details: selectDetails(feature, title.field),
    };
  });

export const buildMapWorkbenchSpiderifyChooserItemFromResolvedSummary = ({
  feature,
  resolved,
  index = 0,
}: {
  feature: MapWorkbenchSpiderifyChooserFeatureLike;
  resolved: MapWorkbenchSpiderifyResolvedSummaryLike;
  index?: number;
}): MapWorkbenchSpiderifyChooserListItem => {
  const fallbackItem = buildMapWorkbenchSpiderifyChooserItems([feature])[0];
  return {
    key: fallbackItem.key || `${feature.sourceLayer ?? feature.layerId ?? 'feature'}:${feature.id ?? index}:${index}`,
    title: resolved.title?.trim() || fallbackItem.title,
    subtitle: resolved.subtitle?.trim() || fallbackItem.subtitle,
    details:
      resolved.summary
        ?.filter(item => item.value?.trim())
        .slice(0, 2)
        .map(item => ({
          label: item.label?.trim() || fallbackItem.subtitle,
          value: item.value.trim(),
        })) ?? fallbackItem.details,
  };
};

export const shouldUseMapWorkbenchSpiderifySheet = ({
  coarsePointer,
  viewportWidth,
}: {
  coarsePointer: boolean;
  viewportWidth?: number | null;
}) => coarsePointer || (typeof viewportWidth === 'number' && viewportWidth <= 768);
